import {
  EmptyInput, InfoList, InputGroupBreak as InputGroupBreakShared, InputRow, ModelDateDMY, ModelNewNumericInput, SVGInfo, WarningInfo,
} from '@equifinance/shared';
import styled from 'styled-components';
import { PureSection } from '../../../../components/Section';
import {
  EmploymentHistory, FinancialYearSet, PartnershipFinancialYearSet, SoleTraderFinancialYearSet,
} from '../../../../../../contexts/account/IncomeAndEmployment';

export const Container = styled.div``;

export const ErrorContainer = styled.div`${({ theme }) => `
  margin-top:${theme.padding[7]};
  border-radius: ${theme.borderRadius[2]};
  box-shadow: 0px 1px 3px 0px rgba(16, 24, 40, 0.10), 0px 1px 2px 0px rgba(16, 24, 40, 0.06);
`}`;

export const PureSectionWrapper = styled(PureSection)` ${({ theme }) => `
  padding-top: ${theme.padding[7]};
`}`;

export const InputGroupBreak = styled(InputGroupBreakShared)`
  margin-left: 0;
  margin-right: 0;
`;

export function FinancialYearSetFields({
  field,
  showNetMonthlyTakeHome,
}: {
  field: string,
  showNetMonthlyTakeHome: boolean
}) {
  return (
    <>
      <InputRow>
        <ModelDateDMY
          label="Financial Year End"
          field={`${field}.financialYearEnd`}
          showDay
          required
        />
        <EmptyInput />
      </InputRow>
      <InputRow>
        <ModelNewNumericInput
          label="Gross annual salary"
          prefix="£"
          field={`${field}.grossAnnualSalary`}
          maxCharacters={10}
          isFixedDecimal
          required
        />
        <ModelNewNumericInput
          label="Annual dividends distributed to applicant"
          prefix="£"
          field={`${field}.annualDividends`}
          maxCharacters={10}
          isFixedDecimal
          required
        />
      </InputRow>
      {showNetMonthlyTakeHome && (
        <InputRow>
          <ModelNewNumericInput
            label="Net monthly take home"
            prefix="£"
            field={`${field}.netMonthlyTakeHome`}
            maxCharacters={10}
            isFixedDecimal
            required
          />
          <EmptyInput />
        </InputRow>
      )}
    </>
  );
}

export function PartnershipFinancialYearSetFields({
  field,
  showNetMonthlyTakeHome,
}: {
  field: string,
  showNetMonthlyTakeHome: boolean
}) {
  return (
    <>
      <InputRow>
        <ModelDateDMY
          label="Financial Year End"
          field={`${field}.financialYearEnd`}
          showDay
          required
        />
        <EmptyInput />
      </InputRow>
      <InputRow>
        <ModelNewNumericInput
          label="Total gross profit before tax"
          prefix="£"
          field={`${field}.totalGrossProfitBeforeTax`}
          maxCharacters={10}
          isFixedDecimal
          required
        />
        <ModelNewNumericInput
          label="Total net profit after tax"
          prefix="£"
          field={`${field}.totalNetProfitAfterTax`}
          maxCharacters={10}
          isFixedDecimal
          required
        />
      </InputRow>
      {showNetMonthlyTakeHome && (
        <InputRow>
          <ModelNewNumericInput
            label="Customer net monthly take home"
            prefix="£"
            field={`${field}.customerNetMonthlyTakeHome`}
            maxCharacters={10}
            isFixedDecimal
            required
          />
          <EmptyInput />
        </InputRow>
      )}
    </>
  );
}

export function SoleTraderFinancialYearSetFields({
  field,
  showNetMonthlyTakeHome,
}: {
  field: string,
  showNetMonthlyTakeHome: boolean
}) {
  return (
    <>
      <InputRow>
        <ModelDateDMY
          label="Financial Year End"
          field={`${field}.financialYearEnd`}
          showDay
          required
        />
        <EmptyInput />
      </InputRow>
      <InputRow>
        <ModelNewNumericInput
          label="Gross profit before tax"
          prefix="£"
          field={`${field}.grossProfitBeforeTax`}
          maxCharacters={10}
          isFixedDecimal
          required
        />
        <ModelNewNumericInput
          label="Net profit after tax"
          prefix="£"
          field={`${field}.netProfitAfterTax`}
          maxCharacters={10}
          isFixedDecimal
          required
        />
      </InputRow>
      {showNetMonthlyTakeHome && (
        <InputRow>
          <ModelNewNumericInput
            label="Net monthly take home"
            prefix="£"
            field={`${field}.netMonthlyTakeHome`}
            maxCharacters={10}
            isFixedDecimal
            required
          />
          <EmptyInput />
        </InputRow>
      )}
    </>
  );
}

export function AccountantCertificateEvidence({
  employmentType,
  evidences,
  field,
}: {
  employmentType: EmploymentHistory['employmentType']
  evidences: FinancialYearSet | PartnershipFinancialYearSet | SoleTraderFinancialYearSet
  field: string,
}) {
  const evidenceList = [evidences.latestCompletedYear, evidences.previousCompletedYear, evidences.currentYearProjection];
  const latestEvidenceIndex = evidenceList.filter(Boolean).length - 1;
  const paths = ['latestCompletedYear', 'previousCompletedYear', 'currentYearProjection'];
  const labels = ['LATEST COMPLETED FINANCIAL YEAR', 'PREVIOUSLY COMPLETED FINANCIAL YEAR (OPTIONAL)', 'CURRENT FINANCIAL YEAR PROJECTION (OPTIONAL)'];
  const fieldPath = (path: string) => `${field}.accountantCertificateEvidence.${path}`;

  const renderFinancialYearSetFields = (field: string, showNetMonthlyTakeHome: boolean = false) => {
    switch (employmentType) {
      case 'DirectorOfLimitedCompany':
      case 'LimitedLiabilityPartnership':
        return <FinancialYearSetFields field={field} showNetMonthlyTakeHome={showNetMonthlyTakeHome} />;
      case 'Partnership':
        return <PartnershipFinancialYearSetFields field={field} showNetMonthlyTakeHome={showNetMonthlyTakeHome} />;
      case 'SoleTrader':
        return <SoleTraderFinancialYearSetFields field={field} showNetMonthlyTakeHome={showNetMonthlyTakeHome} />;
      default: return null;
    }
  };

  return (
    <Container key={evidences.id}>
      {['DirectorOfLimitedCompany', 'LimitedLiabilityPartnership'].includes(employmentType!)
        && <SVGInfo>Figures entered should be based on what the individual receives. Not what the company distributes.</SVGInfo>}
      {
        evidenceList.map((ace, index) => (
          !!ace && (
            <>
              {index === latestEvidenceIndex && (
                <ErrorContainer>
                  <WarningInfo>
                    <InfoList items={['Projection for current year cannot be used for income purposes.']} />
                  </WarningInfo>
                </ErrorContainer>
              )}
              <PureSectionWrapper
                title={labels[index]}
                tooltip={index === latestEvidenceIndex
                  ? `Use of projected figures can only be considered in exceptional cases 
                    and upon a referral. The referral will require a detailed explanation 
                    and evidence from a qualified accountant to support the figures.`
                  : undefined}
              >
                {renderFinancialYearSetFields(fieldPath(paths[index]), index === 0)}
              </PureSectionWrapper>
              {index < latestEvidenceIndex && <InputGroupBreak />}
            </>
          )
        ))
      }
    </Container>
  );
}
